import SearchBar from '@components/SearchBar/SearchBar'
import TokenAvatar from '@components/TokenAvatar'
import { logger } from '@plug-core/logger'
import useConfig from '@plug-core/hooks/useConfig'
import useDebouncedState from '@plug-core/hooks/useDebouncedState'
import useToken from '@plug-core/hooks/useToken'
import { SwapInterface, SwapProvider, SwapTokenPair } from '@plug-core/interfaces/swapInterface'
import { Box, List, ListItem, ListItemAvatar, ListItemText, Stack } from '@mui/material'
import ICPSwap from '@plug-core/clients/ICPSwap'
import KongSwap from '@plug-core/clients/KongSwap'
import getAgent from '@plug-core/utils/getAgent'
import { ROUTES } from '@utils/routes'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ICToken } from '@plug-core/interfaces/common'

interface PairRowProps {
  pair: SwapTokenPair
}

const PairRow: FC<PairRowProps> = ({ pair }) => {
  const navigate = useNavigate()

  const { logo: token0Logo, symbol: token0Symbol } = useToken(pair.token0.principal)
  const { logo: token1Logo, symbol: token1Symbol } = useToken(pair.token1.principal)

  const navigateToFallback = () => {
    navigate(ROUTES.ICPSWAP_FALLBACK_PAIR, { state: { pair } })
  }

  return (
    <ListItem key={pair.swapCanisterPrincipal} onClick={navigateToFallback}>
      <ListItemAvatar>
        <Stack direction="row">
          <TokenAvatar logo={token0Logo} />
          <Box ml={-1}>
            <TokenAvatar logo={token1Logo} />
          </Box>
        </Stack>
      </ListItemAvatar>
      <ListItemText
        primary={`${token0Symbol}/${token1Symbol}`}
        primaryTypographyProps={{ marginLeft: 1 }}
      />
    </ListItem>
  )
}

const PairPicker = () => {
  const [pairs, setPairs] = useState<SwapTokenPair[]>([])
  const [debouncedSearchTerm, setSearchTerm, searchTerm] = useDebouncedState('', 300)

  const { state } = useLocation()
  const { tokens } = useConfig()

  const { t } = useTranslation()

  const dexClient: SwapInterface<ICToken> = useMemo(() => {
    const agent = getAgent()

    if (state.provider === SwapProvider.KONGSWAP) return new KongSwap(agent)

    return new ICPSwap(agent)
  }, [state])

  useEffect(() => {
    const supportedCanisters = tokens.map((token) => token.address)

    dexClient
      .getPairs()
      .then((pairs) => {
        setPairs(
          pairs.filter(
            (pair) =>
              supportedCanisters.includes(pair.token0.principal) &&
              supportedCanisters.includes(pair.token1.principal)
          )
        )
      })
      .catch((e) => logger.error('Failed to get swap fallback pairs', { originalError: e }))
  }, [dexClient, tokens])

  const filteredPairs = useMemo(() => {
    if (!debouncedSearchTerm) return pairs

    return pairs.filter((pair) => {
      const token0 = tokens.find((token) => token.address === pair.token0.principal)
      const token1 = tokens.find((token) => token.address === pair.token1.principal)

      return `${token0?.name?.toLowerCase()}/${token1?.name?.toLowerCase()}`.includes(
        debouncedSearchTerm.toLowerCase()
      )
    })
  }, [debouncedSearchTerm, tokens, pairs])

  return (
    <Stack mx={2}>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder={t('swap.searchPair')}
      />
      <List>
        {filteredPairs.map((pair, idx) => (
          <PairRow key={idx} pair={pair} />
        ))}
      </List>
    </Stack>
  )
}

export default PairPicker
